import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import masterApi from "../api/masterApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import { buildReadableCode } from "../utils/performanceCode";

const FeedbackTypesPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadFeedbackTypes = can(PERFORMANCE_PERMISSIONS.GOALS_READ);
  const canCreateFeedbackTypes = can(PERFORMANCE_PERMISSIONS.GOALS_CREATE);

  if (!canReadFeedbackTypes) {
    return (
      <ModulePageShell
        title="Masters - Feedback Types"
        description="Maintain feedback type catalog used across continuous feedback flows."
      >
        <EmptyState message="You do not have permission to view feedback types." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.feedbackTypes],
    queryFn: () => masterApi.listFeedbackTypes({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: masterApi.createFeedbackType,
    onSuccess: () => {
      toast({
        title: "Feedback type created",
        status: "success",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.feedbackTypes],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create feedback type",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: "",
      anonymousAllowed: true,
      visibility: "MANAGER",
      status: "ACTIVE",
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Masters - Feedback Types"
      description="Maintain feedback type catalog used across continuous feedback flows."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canCreateFeedbackTypes ? (
        <Box
          bg={bg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({
                ...values,
                code: buildReadableCode("FT", values.name),
              }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Feedback Type</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input {...register("name")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Visibility</FormLabel>
                  <Select {...register("visibility")}>
                    <option value="PRIVATE">PRIVATE</option>
                    <option value="MANAGER">MANAGER</option>
                    <option value="EMPLOYEE">EMPLOYEE</option>
                    <option value="HR">HR</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select {...register("status")}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </Select>
                </FormControl>
              </HStack>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Feedback Type
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to feedback types, but cannot create feedback types." />
      )}

      <Box
        bg={bg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No feedback types found." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Type</Th>
                <Th>Description</Th>
                <Th>Visibility</Th>
                <Th>Active</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item.name}</Td>
                  <Td>{item.description || "-"}</Td>
                  <Td>{item.visibility || "-"}</Td>
                  <Td>{item.status === "ACTIVE" ? "Yes" : "No"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default FeedbackTypesPage;
